// ** React Imports
import { useEffect, useState } from 'react'

// ** MUI Imports
import Drawer from '@mui/material/Drawer'
import Select from '@mui/material/Select'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'

// ** Third Party Imports
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller } from 'react-hook-form'

// ** Icons Imports
import Close from 'mdi-material-ui/Close'

// ** Store Imports
import { client } from 'src/utils/axios-client';
import { addUserApiEndpoint, roleOptionsApiEndpoint } from 'src/configs/endpoints';
import { SelectOption } from 'src/types/common';
import { Alert } from '@mui/material'
import AlertError from '../../../../../components/AlertError'
import { ApiError } from '../../../../../types/ApplicationTypes'

// ** Actions Imports


// ** Types Imports


interface SidebarAddUserType {
    open: boolean
    toggle: () => void
}

interface UserData {
    email: string
    company: string
    country: string
    contact: number
    fullName: string
    username: string
}

const showErrors = (field: string, valueLen: number, min: number) => {
    if (valueLen === 0) {
        return `${field} field is required`
    } else if (valueLen > 0 && valueLen < min) {
        return `${field} must be at least ${min} characters`
    } else {
        return ''
    }
}

const Header = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default
}))

const schema = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().email().required(),
    role: yup.string().required(),
})

const defaultValues = {
    email: '',
    firstName: '',
    lastName: '',
    role: ''
}

const SidebarAddUser = (props: SidebarAddUserType) => {
    // ** Props
    const { open, toggle } = props
    const [error, setError] = useState<null | ApiError>(null);
    const [roles, setRoles] = useState([] as SelectOption[])
    
    const {
        reset,
        control,
        setValue,        
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(schema)
    })

    useEffect(() => {
        const loadRoles = async () => {
            try {
                var response = await client.get(roleOptionsApiEndpoint);
                setRoles(response.data);
            }
            catch (err) {
                setError(err);
            }

        }

        loadRoles();
    }, [])

    const onSubmit = async (data: any) => {
        try {
            const response = await client.post('/api/admin/users', { ...data })
            toggle()
            reset()
        } catch (err) {
            setError(err);
        }
    }

    const handleClose = () => {
        setValue('role', '')
        setValue('firstName', '')
        setValue('lastName', '')
        setValue('email', '')

        toggle()
        reset()
    }



    return (
        <Drawer
            open={open}
            anchor='right'
            variant='temporary'
            onClose={handleClose}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: { xs: 300, sm: 400 } } }}
        >
            <Header>
                <Typography variant='h6'>Add User</Typography>
                <Close fontSize='small' onClick={handleClose} sx={{ cursor: 'pointer' }} />
            </Header>
            <Box sx={{ p: 5 }}>
                {error && <AlertError error={error} sx={{ mb: 4 }} />}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <Controller
                            name='firstName'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size="small"
                                    value={value}
                                    label='First Name'
                                    onChange={onChange}
                                    placeholder='First Name'
                                    error={Boolean(errors.firstName)}
                                />
                            )}
                        />
                        {errors.firstName && <FormHelperText sx={{ color: 'error.main' }}>{errors.firstName.message}</FormHelperText>}
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <Controller
                            name='lastName'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size="small"
                                    value={value}
                                    label='Last Name'
                                    onChange={onChange}
                                    placeholder='Last Name'
                                    error={Boolean(errors.lastName)}
                                />
                            )}
                        />
                        {errors.lastName && <FormHelperText sx={{ color: 'error.main' }}>{errors.lastName.message}</FormHelperText>}
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 6 }}>
                        <Controller
                            name='email'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size="small"
                                    type='email'
                                    value={value}
                                    label='Email'
                                    onChange={onChange}
                                    placeholder='johndoe@email.com'
                                    error={Boolean(errors.email)}
                                />
                            )}
                        />
                        {errors.email && <FormHelperText sx={{ color: 'error.main' }}>{errors.email.message}</FormHelperText>}
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <InputLabel>Select Role</InputLabel>
                        <Controller
                            name='role'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    label='Select Role'
                                    size="small"
                                    fullWidth
                                    value={value}
                                    onChange={onChange}
                                    error={Boolean(errors.role)}
                                >
                                    {roles.map(role => <MenuItem key={role.value} value={role.value}>{role.name}</MenuItem>)}
                                </Select>
                            )}
                        />
                    </FormControl>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button size='small' type='submit' variant='contained' sx={{ mr: 3 }} onClick={handleSubmit(onSubmit)}>
                            Submit
                        </Button>
                        <Button size='small' variant='outlined' color='secondary' onClick={handleClose}>
                            Cancel
                        </Button>
                    </Box>
                </form>
            </Box>
        </Drawer>
    )
}

export default SidebarAddUser
